import * as Sentry from '@sentry/tanstackstart-react'
import { useState, useEffect, createContext, useContext, useRef } from 'react'
import { api } from '../../lib/api'
import WelcomeStep from './WelcomeStep'
import HelperAppStep from './HelperAppStep'
import RulesStep from './RulesStep'
import SchedulesStep from './SchedulesStep'
import AddDeviceStep from './AddDeviceStep'
import OnboardingProgress from './OnboardingProgress'

const STORAGE_KEY = 'unbreakable_onboarding'

export type OnboardingStep = 'welcome' | 'helper' | 'device' | 'rules' | 'schedules' | 'complete'

const STEPS: OnboardingStep[] = ['welcome', 'helper', 'device', 'rules', 'schedules', 'complete']

interface OnboardingState {
  currentStep: OnboardingStep
  completedSteps: OnboardingStep[]
  isComplete: boolean
}

interface OnboardingContextValue {
  state: OnboardingState
  goToStep: (step: OnboardingStep) => void
  completeStep: (step: OnboardingStep) => void
  nextStep: () => Promise<void>
  prevStep: () => void
  skipOnboarding: () => void
  resetOnboarding: () => void
  validationError: string | null
  clearValidationError: () => void
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null)

export const useOnboarding = () => {
  const context = useContext(OnboardingContext)
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingFlow')
  }
  return context
}

const getLocalState = (): OnboardingState | null => {
  if (typeof window === 'undefined') return null

  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      if (parsed && typeof parsed.currentStep === 'string' && typeof parsed.isComplete === 'boolean') {
        return parsed
      }
    }
  } catch {
    Sentry.logger.error('Failed to load onboarding state from localStorage')
  }

  return null
}

interface OnboardingFlowProps {
  children: React.ReactNode
}

export default function OnboardingFlow({ children }: OnboardingFlowProps) {
  const [state, setState] = useState<OnboardingState>({
    currentStep: 'welcome',
    completedSteps: [],
    isComplete: false,
  })
  const [isLoaded, setIsLoaded] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)
  const initStarted = useRef(false)

  useEffect(() => {
    if (initStarted.current) return
    initStarted.current = true

    const initializeOnboarding = async () => {
      const localState = getLocalState()

      if (localState?.isComplete) {
        setState(localState)
        setIsLoaded(true)
        return
      }

      try {
        const response = await api.getOnboardingStatus()
        if (response.onboardingCompleted) {
          const completeState = {
            currentStep: 'complete' as OnboardingStep,
            completedSteps: STEPS.filter(s => s !== 'complete') as OnboardingStep[],
            isComplete: true,
          }
          setState(completeState)
          localStorage.setItem(STORAGE_KEY, JSON.stringify(completeState))
          setIsLoaded(true)
          return
        }
      } catch (err) {
        Sentry.captureException(err, { tags: { component: 'OnboardingFlow', context: 'fetch_status' } })
        Sentry.logger.error('Failed to fetch onboarding status from backend')
      }

      if (localState) {
        setState(localState)
      }

      setIsLoaded(true)
    }

    initializeOnboarding()
  }, [])

  useEffect(() => {
    if (isLoaded) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
    }
  }, [state, isLoaded])

  const goToStep = (step: OnboardingStep) => {
    setState(prev => ({ ...prev, currentStep: step }))
  }

  const completeStep = (step: OnboardingStep) => {
    setState(prev => ({
      ...prev,
      completedSteps: prev.completedSteps.includes(step)
        ? prev.completedSteps
        : [...prev.completedSteps, step],
    }))
  }

  const getPrerequisites = async () => {
    try {
      return await api.getOnboardingPrerequisites()
    } catch (err) {
      Sentry.captureException(err, { tags: { component: 'OnboardingFlow', context: 'prerequisites' } })
      Sentry.logger.error('Failed to check onboarding prerequisites')
      return { hasDevices: false, hasPolicies: false, hasSchedules: false }
    }
  }

  const markOnboardingComplete = async () => {
    const completeState = {
      currentStep: 'complete' as OnboardingStep,
      completedSteps: STEPS.filter(s => s !== 'complete') as OnboardingStep[],
      isComplete: true,
    }

    setState(completeState)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(completeState))

    api.completeOnboarding().catch(err => {
      Sentry.captureException(err, { tags: { component: 'OnboardingFlow', context: 'complete_sync' } })
      Sentry.logger.error('Failed to sync onboarding completion to backend')
    })
  }

  const nextStep = async () => {
    setValidationError(null)

    const currentIndex = STEPS.indexOf(state.currentStep)
    const nextIndex = currentIndex + 1

    if (nextIndex >= STEPS.length) {
      return
    }

    const nextStepValue = STEPS[nextIndex]
    const currentStepValue = state.currentStep

    if (nextStepValue === 'rules') {
      const prerequisites = await getPrerequisites()
      if (!prerequisites.hasDevices) {
        setValidationError('Please add at least one device before creating rules.')
        return
      }
    } else if (nextStepValue === 'schedules') {
      const prerequisites = await getPrerequisites()
      if (!prerequisites.hasDevices) {
        setValidationError('Please add at least one device before creating schedules.')
        return
      }
      if (!prerequisites.hasPolicies) {
        setValidationError('Please create at least one rule before creating schedules.')
        return
      }
    } else if (nextStepValue === 'complete') {
      const prerequisites = await getPrerequisites()
      if (!prerequisites.hasDevices) {
        setValidationError('Please add at least one device to complete onboarding.')
        return
      }
      if (!prerequisites.hasPolicies) {
        setValidationError('Please create at least one rule to complete onboarding.')
        return
      }
      if (!prerequisites.hasSchedules) {
        setValidationError('Please create at least one schedule to complete onboarding.')
        return
      }
    }

    completeStep(currentStepValue)

    if (nextStepValue === 'complete') {
      markOnboardingComplete()
    } else {
      goToStep(nextStepValue)
    }
  }

  const clearValidationError = () => {
    setValidationError(null)
  }

  const prevStep = () => {
    const currentIndex = STEPS.indexOf(state.currentStep)
    if (currentIndex > 0) {
      goToStep(STEPS[currentIndex - 1])
    }
  }

  const skipOnboarding = () => {
    markOnboardingComplete()
  }

  const resetOnboarding = () => {
    const initialState = {
      currentStep: 'welcome' as OnboardingStep,
      completedSteps: [] as OnboardingStep[],
      isComplete: false,
    }

    setState(initialState)
    localStorage.removeItem(STORAGE_KEY)

    api.resetOnboarding().catch(err => {
      Sentry.captureException(err, { tags: { component: 'OnboardingFlow', context: 'reset_sync' } })
      Sentry.logger.error('Failed to reset onboarding in backend')
    })
  }

  const contextValue: OnboardingContextValue = {
    state,
    goToStep,
    prevStep,
    completeStep,
    nextStep,
    skipOnboarding,
    resetOnboarding,
    validationError,
    clearValidationError,
  }

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-page-bg">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand" />
      </div>
    )
  }

  if (state.isComplete) {
    return (
      <OnboardingContext.Provider value={contextValue}>
        {children}
      </OnboardingContext.Provider>
    )
  }

  return (
    <OnboardingContext.Provider value={contextValue}>
      <div className="min-h-screen bg-page-bg flex flex-col">
        <OnboardingProgress
          steps={STEPS.filter(s => s !== 'complete')}
          currentStep={state.currentStep}
          onBack={prevStep}
        />

        <div className="flex-1 flex items-start justify-center px-4 py-6">
          <div className="w-full max-w-2xl">
            {state.currentStep === 'welcome' && <WelcomeStep />}
            {state.currentStep === 'helper' && <HelperAppStep />}
            {state.currentStep === 'rules' && <RulesStep />}
            {state.currentStep === 'schedules' && <SchedulesStep />}
            {state.currentStep === 'device' && <AddDeviceStep />}
          </div>
        </div>
      </div>
    </OnboardingContext.Provider>
  )
}
